import React, { useState, useEffect, useRef } from 'react';
import { Building2, RefreshCw, Search, Check } from 'lucide-react';
import { api } from '../services/api';
import { cn } from '../utils/cn';

interface Department {
  id: string;
  name: string;
  employee_count?: number;
}

/**
 * Relation types a department membership can carry. Mirrors models.DepartmentRelationTypes in
 * condo-manager — proprietario/inquilino are shared with the residential vocabulary (does the
 * company own or rent its space), responsavel marks the department head, funcionario is the
 * default regular employee.
 */
export const DEPARTMENT_RELATION_OPTIONS: { value: string; label: string }[] = [
  { value: 'proprietario', label: 'Proprietário' },
  { value: 'inquilino', label: 'Inquilino' },
  { value: 'responsavel', label: 'Responsável' },
  { value: 'funcionario', label: 'Funcionário' },
];

interface DepartmentManagerProps {
  value: string[];
  /**
   * Relation per department id. Supplying this together with onRelationChange turns each selected
   * row into "department + its relation" — a department membership is its own PersonRole server
   * side, so two departments can genuinely hold two different relations for the same person.
   * Omit both and the component stays the plain checkbox list other callers expect.
   */
  relations?: Record<string, string>;
  onRelationChange?: (departmentId: string, relationType: string) => void;
  customerId: string;
  onChange: (departmentIds: string[]) => void;
  darkMode?: boolean;
}

export const DepartmentManager: React.FC<DepartmentManagerProps> = ({
  value,
  relations,
  onRelationChange,
  customerId,
  onChange,
  darkMode = false,
}) => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchDepartments = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/customers/${customerId}/departments`);
        const depts = response.data?.data || response.data || [];
        setDepartments(Array.isArray(depts) ? depts : []);
      } catch (err) {
        console.error('Failed to fetch departments:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDepartments();
  }, [customerId]);

  const toggle = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter(v => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const clearAll = () => onChange([]);

  const filtered = departments.filter(d =>
    d.name.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className={cn('rounded-lg border overflow-hidden', darkMode ? 'border-gray-700' : 'border-gray-200')}>

      {/* Header */}
      <div className={cn(
        'flex items-center gap-2 px-3 py-2.5 border-b',
        darkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-gray-50 border-gray-200'
      )}>
        <Building2 className={cn('w-3.5 h-3.5 flex-shrink-0', darkMode ? 'text-gray-500' : 'text-gray-400')} />
        <span className={cn('text-[10px] font-semibold uppercase tracking-widest flex-1', darkMode ? 'text-gray-500' : 'text-gray-400')}>
          Departamentos
        </span>
        {value.length > 0 && (
          <span className={cn(
            'text-[10px] font-medium px-1.5 py-0.5 rounded-full',
            darkMode ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-50 text-blue-600'
          )}>
            {value.length} {value.length === 1 ? 'selecionado' : 'selecionados'}
          </span>
        )}
      </div>

      {/* Search bar */}
      {!loading && departments.length > 3 && (
        <div className={cn(
          'flex items-center gap-2 px-3 py-2 border-b',
          darkMode ? 'border-gray-700 bg-gray-800/40' : 'border-gray-100 bg-white'
        )}>
          <Search className={cn('w-3.5 h-3.5 flex-shrink-0', darkMode ? 'text-gray-500' : 'text-gray-400')} />
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar departamento..."
            className={cn(
              'flex-1 text-xs bg-transparent outline-none placeholder:text-gray-400',
              darkMode ? 'text-gray-200' : 'text-gray-700'
            )}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className={cn('text-xs', darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600')}
            >
              ×
            </button>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className={cn('flex items-center gap-2 px-3 py-4 text-xs', darkMode ? 'text-gray-500' : 'text-gray-400')}>
          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          Carregando...
        </div>
      )}

      {/* "Sem Departamento" — clear option */}
      {!loading && (
        <button
          type="button"
          onClick={clearAll}
          className={cn(
            'w-full flex items-center gap-3 px-3 py-2.5 text-xs transition-colors border-b',
            darkMode ? 'border-gray-800 hover:bg-gray-800/40' : 'border-gray-100 hover:bg-gray-50',
            value.length === 0
              ? (darkMode ? 'text-gray-200' : 'text-gray-900')
              : (darkMode ? 'text-gray-500' : 'text-gray-400')
          )}
        >
          <span className={cn(
            'w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-colors',
            value.length === 0
              ? (darkMode ? 'bg-blue-600 border-blue-600' : 'bg-blue-600 border-blue-600')
              : (darkMode ? 'border-gray-600' : 'border-gray-300')
          )}>
            {value.length === 0 && <Check className="w-3 h-3 text-white" />}
          </span>
          <span className={value.length === 0 ? 'font-medium' : ''}>Sem Departamento</span>
        </button>
      )}

      {/* Department list */}
      {!loading && (
        <div className={cn('divide-y overflow-y-auto max-h-52', darkMode ? 'divide-gray-800' : 'divide-gray-100')}>
          {filtered.length === 0 && (
            <p className={cn('px-3 py-4 text-xs text-center', darkMode ? 'text-gray-600' : 'text-gray-400')}>
              Nenhum resultado para "{query}"
            </p>
          )}
          {filtered.map(dept => {
            const selected = value.includes(dept.id);
            return (
              <div
                key={dept.id}
                data-testid="department-row"
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 text-xs text-left transition-colors',
                  selected
                    ? (darkMode ? 'bg-blue-900/20' : 'bg-blue-50/60')
                    : (darkMode ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50')
                )}
              >
              <button
                type="button"
                onClick={() => toggle(dept.id)}
                className="flex-1 flex items-center gap-3 text-left min-w-0"
              >
                <span className={cn(
                  'w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-colors',
                  selected
                    ? (darkMode ? 'bg-blue-600 border-blue-600' : 'bg-blue-600 border-blue-600')
                    : (darkMode ? 'border-gray-600' : 'border-gray-300')
                )}>
                  {selected && <Check className="w-3 h-3 text-white" />}
                </span>
                <span className={cn(
                  'flex-1',
                  selected
                    ? (darkMode ? 'text-gray-100 font-medium' : 'text-gray-900 font-medium')
                    : (darkMode ? 'text-gray-400' : 'text-gray-600')
                )}>
                  {dept.name}
                </span>
                {dept.employee_count !== undefined && dept.employee_count > 0 && (
                  <span className={cn('text-[10px]', darkMode ? 'text-gray-600' : 'text-gray-400')}>
                    {dept.employee_count}
                  </span>
                )}
              </button>

              {/* Relation for THIS department. Rendered outside the toggle button — nesting a
                  select inside it would both toggle the department and change the relation on a
                  single click. */}
              {onRelationChange && selected && (
                <select
                  value={relations?.[dept.id] ?? ''}
                  onChange={e => onRelationChange(dept.id, e.target.value)}
                  aria-label={`Tipo de relação em ${dept.name}`}
                  className={cn(
                    // pl/pr asymmetry is the fix for the caret overlapping the label: a native
                    // select draws its arrow INSIDE the padding box, so a uniform px-1.5 left it
                    // sitting on top of "Definir tipo". pr-6 reserves the arrow's lane, and the
                    // min-width stops the longest option ("Proprietário") from squeezing it back.
                    'text-[11px] rounded border pl-2 pr-6 py-1 flex-shrink-0 min-w-[7.5rem]',
                    'focus:outline-none focus:ring-1 focus:ring-blue-400',
                    darkMode
                      ? 'bg-gray-900 border-gray-700 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-700'
                  )}
                >
                  <option value="">Definir tipo</option>
                  {DEPARTMENT_RELATION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
